import BillingStore from 'billing/BillingStore'
import BillingMethod from 'billing/models/BillingMethod'
import BillingProfile from 'billing/models/BillingProfile'
import { NewPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import Team from 'iam/models/Team'
import { GetCurrentMember } from 'iam/SessionStore'
import { CancellationToken } from 'infra/cancellation'
import { computed, makeObservable } from 'mobx'
import { Id } from 'types/definitions'
import { task, TaskGroup } from 'mobx-task'

export default class TeamPaymentsViewModel {
  /**
   * Task group.
   * @private
   */
  private tasks

  constructor(
    private getCurrentMember: GetCurrentMember,
    public team: Team,
    private billingStore: BillingStore,
    private newPaymentMethod: NewPaymentMethodDelegate
  ) {
    makeObservable(this)
    this.tasks = TaskGroup<any, any>([
      this.activate,
      this.addPaymentMethod,
      this.setDefaultPaymentMethod,
    ])
  }

  activate = task(async () => {
    await this.billingStore.fetchBillingProfileForTeam(Id.assert(this.team.id))
  })

  @computed
  get disabled() {
    return this.tasks.pending
  }

  @computed
  get billingProfile() {
    return (
      this.billingStore.teamBillingProfile(this.team) ?? BillingProfile.none
    )
  }

  @computed
  get paymentMethods() {
    return this.billingProfile.billingMethods.slice()
  }

  @computed
  get canManageTeamBilling() {
    return this.billingProfile.allowedToManage
  }

  /**
   * Add a team payment method.
   */
  addPaymentMethod = task.resolved(async () => {
    const method = await this.newPaymentMethod(
      this.getCurrentMember(this.team.workspace.id),
      this.team,
      CancellationToken.default
    )
    if (method) {
      await this.setDefaultPaymentMethod(method)
    }
  })

  /**
   * Sets the default team billing method.
   * @param {*} method
   */
  setDefaultPaymentMethod = task.resolved(async (method: BillingMethod) => {
    await this.billingStore.changeDefaultBillingMethod(method, this.team)
  })
}
